import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { networkService } from "../../networking/network-services";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useMediaQuery } from "@mui/material";
import LineZoneEditor from "./LineZoneEditor";
import ProgressScale from "../video-player/progressScale";
import dayjs from "dayjs";
import { getDeviceSnapshotsRange } from "../../redux/actions/service-action";
import {
  RecordingTimeline,
  RecordingTimelineContext,
  STATUS_COLORS,
  TimelineSlot,
  TimelineSnapshot,
  hhmmssToSeconds,
  minuteStatuses,
} from "./TimelineContext";

// Recording status and snapshots come from the server (boards push them every
// minute), so the timeline still shows when the board itself is offline. There
// is one strip: the player's own (ProgressScale), coloured from this data and
// carrying the snapshot markers; in Image mode the same strip sits under the
// photo and picks the nearest snapshot.

type Media = "video" | "image" | null;

const hhmm = (t: string) => `${t.slice(0, 2)}:${t.slice(2, 4)}`;

// Video / Image toggle in the camera header.
const MediaButton: React.FC<{ on: boolean; label: string; Icon: any; onClick?: () => void; disabled?: boolean; title?: string }> = ({
  on,
  label,
  Icon,
  onClick,
  disabled,
  title,
}) => {
  const Chevron = on ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  return (
    <button
      type="button"
      aria-expanded={on}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={{
        height: 32,
        padding: "0 12px",
        borderRadius: 6,
        border: `1px solid ${on ? "#ED6926" : "#d1d5db"}`,
        background: on ? "#ED6926" : "#fff",
        color: on ? "#fff" : "#1f2937",
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
      {label}
      <Chevron sx={{ fontSize: 16 }} />
    </button>
  );
};

// The snapshot is cropped to fill a box sized so that photo, thumbnails and
// strip together take the video panel's height (player 65vh plus controls);
// 35vh on a phone. Fullscreen shows the whole photo.
const SnapshotFrame: React.FC<{
  url: string;
  caption: string;
  downloadName: string;
  tunnelUrl?: string;
  userName?: string;
  startEditing?: boolean;
}> = ({ url, caption, downloadName, tunnelUrl, userName, startEditing = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isPhone = useMediaQuery("(max-width:768px)");
  const [full, setFull] = useState(false);
  // Line/zone editing shows the whole photo (contain) so clicks map 1:1 to its pixels.
  const [editing, setEditing] = useState(startEditing);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [nat, setNat] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFull = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await ref.current?.requestFullscreen();
    } catch (e) {
      // fullscreen refused (e.g. iframe without permission): stay inline
    }
  };
  const chip: React.CSSProperties = { position: "absolute", background: "#fff", color: "#1f2937", fontSize: 12, padding: "3px 8px", borderRadius: 4, display: "flex", alignItems: "center", gap: 4, textDecoration: "none", border: "none", cursor: "pointer" };
  return (
    <div ref={ref} style={{ position: "relative", background: "#111", borderRadius: full ? 0 : 4, overflow: "hidden", height: full ? "100vh" : isPhone ? "35vh" : "max(200px, calc(65vh - 150px))" }}>
      <img
        src={url}
        alt={`Camera snapshot ${caption}`}
        onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        onDoubleClick={editing ? undefined : toggleFull}
        style={{ width: "100%", height: "100%", display: "block", objectFit: full || editing ? "contain" : "cover" }}
      />
      {tunnelUrl !== undefined && (
        <LineZoneEditor
          url={url}
          tunnelUrl={tunnelUrl}
          userName={userName}
          frameW={box.w}
          frameH={box.h}
          naturalW={nat.w}
          naturalH={nat.h}
          active={editing}
          setActive={setEditing}
        />
      )}
      <span style={{ position: "absolute", top: 8, left: 10, background: "rgba(31,41,55,.85)", color: "#fff", fontSize: 12, padding: "3px 8px", borderRadius: 4, fontFamily: "'Roboto Mono', monospace" }}>
        Snapshot {caption}
      </span>
      {/* Hidden while editing: they sit on the photo and would catch drawing clicks. */}
      {!editing && (
        <>
          <a href={url} download={downloadName} style={{ ...chip, bottom: 8, right: 92 }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} /> Download
          </a>
          <button type="button" onClick={toggleFull} title={full ? "Exit fullscreen" : "Fullscreen"} style={{ ...chip, bottom: 8, right: 10 }}>
            {full ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
            {full ? "Exit" : "Full"}
          </button>
        </>
      )}
    </div>
  );
};

interface Props {
  uuid?: string;
  date: string; // YYYY-MM-DD
  range?: { start: string; end: string } | null; // multi-day: snapshots only, no video
  children: React.ReactNode; // the existing video player
}

interface RangeSnapshot {
  date: string;
  time: string;
  url: string;
}

interface RangeDay {
  date: string;
  full: number;
  partial: number;
  none: number;
}

const hm = (minutes: number) => `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const CameraSection: React.FC<Props> = ({ uuid, date, range, children }) => {
  if (range) return <RangeCameraSection uuid={uuid} range={range} />;
  return <DayCameraSection uuid={uuid} date={date}>{children}</DayCameraSection>;
};

// Range mode: every snapshot of up to 20 days, newest first, with how much of
// each day was recorded. Video is a single-day thing, so there is none here.
const RangeCameraSection: React.FC<{ uuid?: string; range: { start: string; end: string } }> = ({ uuid, range }) => {
  const loggedIn = useSelector((s: any) => !!s?.auth?.loginDetails?.user_details?.[0]?.access_token);
  const [snaps, setSnaps] = useState<RangeSnapshot[]>([]);
  const [days, setDays] = useState<RangeDay[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const thumbsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSnaps([]);
    setDays([]);
    setSelected(null);
    if (!uuid || !loggedIn) return;
    let cancelled = false;
    setLoading(true);
    getDeviceSnapshotsRange({ startDate: range.start, endDate: range.end, uid: uuid })
      .then((res: any) => {
        if (cancelled || !res) return;
        setSnaps(res.snapshots || []);
        setDays(res.days || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, range.start, range.end, loggedIn]);

  const key = (s: RangeSnapshot) => `${s.date} ${s.time}`;
  const newestFirst = useMemo(() => [...snaps].reverse(), [snaps]);
  const current = newestFirst.find((s) => key(s) === selected) || newestFirst[0];
  const recorded = days.reduce((sum, d) => sum + d.full + d.partial, 0);
  const [showImage, setShowImage] = useState(true);
  // Every day of the range gets a block, recorded or not, so a gap is visible.
  const allDays = useMemo(() => {
    const byDate = new Map(days.map((d) => [d.date, d]));
    const out: RangeDay[] = [];
    for (let d = dayjs(range.start); !d.isAfter(dayjs(range.end), "day"); d = d.add(1, "day")) {
      const k = d.format("YYYY-MM-DD");
      out.push(byDate.get(k) || { date: k, full: 0, partial: 0, none: 0 });
    }
    return out;
  }, [days, range.start, range.end]);

  // Clicking a day shows that day's newest snapshot and scrolls its thumbnail into view.
  const pickDay = (d: string) => {
    const s = newestFirst.find((x) => x.date === d);
    if (!s) return;
    setSelected(key(s));
    const el = thumbsRef.current?.querySelector(`[data-snap="${key(s)}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const label = (s: RangeSnapshot) => `${dayjs(s.date).format("D MMM")} ${hhmm(s.time)}`;

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fafafa", color: "#1f2937" }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Camera</span>
        <span style={{ fontSize: 12, color: "#4b5563", marginLeft: 12, marginRight: "auto" }}>
          <span style={{ fontWeight: 500, color: "#1f2937" }}>
            {dayjs(range.start).format("D MMM")} – {dayjs(range.end).format("D MMM")}
          </span>
          {` · ${hm(recorded)} recorded · ${snaps.length} snapshots`}
        </span>
        <MediaButton on={false} label="Video" Icon={VideocamOutlinedIcon} disabled title="Video plays one day at a time. Pick a single day to watch it." />
        <MediaButton on={showImage} label="Image" Icon={ImageOutlinedIcon} onClick={() => setShowImage((v) => !v)} />
      </div>
      <div style={{ borderTop: "1px solid #e5e7eb", padding: 10, display: "flex", flexDirection: "column", gap: 8, color: "#1f2937" }}>
        {!showImage ? null : current ? (
          <SnapshotFrame url={current.url} caption={label(current)} downloadName={`${uuid}-${current.date}-${current.time}.jpg`} />
        ) : (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280", fontSize: 13, background: "#f3f4f6", borderRadius: 4 }}>
            {loading ? "Loading snapshots…" : "No snapshot in this range."}
          </div>
        )}
        {showImage && newestFirst.length > 0 && (
          <div ref={thumbsRef} style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
            {newestFirst.map((s) => {
              const on = current && key(s) === key(current);
              return (
                <button
                  key={key(s)}
                  data-snap={key(s)}
                  type="button"
                  onClick={() => setSelected(key(s))}
                  style={{ flex: "0 0 auto", width: 106, padding: 0, border: `2px solid ${on ? "#ED6926" : "#e5e7eb"}`, borderRadius: 4, background: "#fff", cursor: "pointer", overflow: "hidden" }}
                >
                  <img src={s.url} alt={`Snapshot ${label(s)}`} loading="lazy" style={{ width: "100%", height: 58, objectFit: "cover", display: "block", background: "#111" }} />
                  <span style={{ display: "block", fontSize: 11, padding: "3px 0", color: "#1f2937", fontFamily: "'Roboto Mono', monospace" }}>{label(s)}</span>
                </button>
              );
            })}
          </div>
        )}
        {(
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {allDays.map((d) => {
              const rec = d.full + d.partial;
              const hasSnap = snaps.some((s) => s.date === d.date);
              const on = current?.date === d.date;
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => pickDay(d.date)}
                  title={rec ? `${d.date}: full ${d.full}m, partial ${d.partial}m, none ${d.none}m` : `${d.date}: no recording reported`}
                  style={{ flex: "0 0 92px", padding: "4px 2px", border: `1px solid ${on ? "#ED6926" : "#e5e7eb"}`, borderRadius: 4, background: rec ? "#fff" : "#f9fafb", cursor: hasSnap ? "pointer" : "default", fontSize: 11, color: rec ? "#1f2937" : "#9ca3af" }}
                >
                  <div style={{ fontWeight: 500 }}>{dayjs(d.date).format("D MMM")}</div>
                  <div style={{ height: 6, margin: "3px 4px", background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, (rec / 1440) * 100)}%`, height: "100%", background: STATUS_COLORS.full }} />
                  </div>
                  <div style={{ color: rec ? "#6b7280" : "#9ca3af" }}>{rec ? hm(rec) : "No recording"}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DayCameraSection: React.FC<{ uuid?: string; date: string; children: React.ReactNode }> = ({ uuid, date, children }) => {
  // Only whether someone is logged in matters here: networkService reads the
  // current token itself, and a refreshed token must not reset the timeline.
  const loggedIn = useSelector((s: any) => !!s?.auth?.loginDetails?.user_details?.[0]?.access_token);
  const tunnelUrl: string | undefined = useSelector((s: any) => s?.service?.activeCarDetails?.raw?.tunnel_info?.[0]);
  const userName: string = useSelector((s: any) => {
    const u = s?.auth?.loginDetails?.user_details?.[0] || {};
    return u.name || u.full_name || u.username || u.email || "";
  });
  const [media, setMedia] = useState<Media>("video");
  // A frame grabbed from the playing video, for drawing lines/zones on boards
  // that send no snapshots: the board is online, so the video is there.
  const videoBoxRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<{ url: string; label: string } | null>(null);
  const [frameMsg, setFrameMsg] = useState("");
  // A grabbed frame belongs to one camera and day; another device must never
  // show it (its lines would be drawn on the wrong picture).
  useEffect(() => {
    setFrame(null);
    setFrameMsg("");
  }, [uuid, date]);
  const grabFrame = () => {
    const v = videoBoxRef.current?.querySelector("video") as HTMLVideoElement | null;
    if (!v || !v.videoWidth) {
      setFrameMsg("Play the video first, then draw on its frame.");
      setMedia("video");
      return;
    }
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext("2d")!.drawImage(v, 0, 0);
      v.pause();
      setFrame({ url: c.toDataURL("image/jpeg", 0.92), label: `video frame ${dayjs().format("h:mm A")}` });
      setFrameMsg("");
      setMedia("image");
    } catch (e) {
      setFrameMsg("This video cannot be captured in the browser.");
    }
  };
  const [slots, setSlots] = useState<TimelineSlot[]>([]);
  const [snapshots, setSnapshots] = useState<TimelineSnapshot[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    // Clear first, so another device's or day's timeline never lingers.
    setSelected(null);
    setSlots([]);
    setSnapshots([]);
    if (!uuid || !date || !loggedIn) return;
    let cancelled = false;
    const load = async () => {
      try {
        // networkService adds the token and refreshes it on a 401.
        const { data } = await networkService.request({
          method: "GET",
          url: `api/devices/${uuid}/timeline/`,
          params: { date },
        });
        if (cancelled) return;
        setSlots(data?.slots || []);
        setSnapshots(data?.snapshots || []);
      } catch (e) {
        // A failed refresh keeps what is already on screen.
      }
    };
    load();
    const isToday = date === new Date().toLocaleDateString("en-CA");
    const timer = isToday ? window.setInterval(load, 60000) : undefined;
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [uuid, date, loggedIn]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { full: 0, partial: 0, none: 0 };
    minuteStatuses(slots, date).forEach((st) => {
      if (st) c[st]++;
    });
    return c;
  }, [slots, date]);
  const recorded = counts.full + counts.partial;

  const current = snapshots.find((s) => s.time === selected) || snapshots[snapshots.length - 1];

  const openSnapshot = useCallback((time: string) => {
    setSelected(time);
    setMedia("image");
  }, []);

  const timeline: RecordingTimeline = useMemo(
    () => ({
      date,
      slots,
      snapshots,
      selectedSnapshot: media === "image" ? current?.time ?? null : null,
      openSnapshot,
    }),
    [date, slots, snapshots, media, current?.time, openSnapshot]
  );

  // Image mode: a click anywhere on the strip shows the nearest snapshot. The
  // strip resets itself to 0 when the device/date changes; that reset is not a
  // click, so a 0 is ignored rather than jumping to the first photo.
  const pickNearest = useRef((t: number) => {});
  pickNearest.current = (t: number) => {
    if (t === 0 || !snapshots.length) return;
    let best = snapshots[0];
    snapshots.forEach((s) => {
      if (Math.abs(hhmmssToSeconds(s.time) - t) < Math.abs(hhmmssToSeconds(best.time) - t)) best = s;
    });
    setSelected(best.time);
  };
  const onImageStripChange = useCallback((t: number) => pickNearest.current(t), []);

  const toggle = (m: Exclude<Media, null>) => setMedia((prev) => (prev === m ? null : m));

  const mediaButton = (m: Exclude<Media, null>, label: string, Icon: any) => (
    <MediaButton on={media === m} label={label} Icon={Icon} onClick={() => toggle(m)} />
  );

  const legend = (k: "full" | "partial" | "none", label: string) => (
    <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 11, height: 9, borderRadius: 2, background: STATUS_COLORS[k] }} />
      {label} {counts[k]}m
    </span>
  );

  return (
    <RecordingTimelineContext.Provider value={timeline}>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fafafa", color: "#1f2937" }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Camera</span>
          <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 12, color: "#4b5563", marginRight: "auto", marginLeft: 12 }}>
            <span style={{ fontWeight: 500, color: "#1f2937" }}>
              {slots.length ? `${Math.floor(recorded / 60)}h ${recorded % 60}m recorded` : "No recording report for this day"}
            </span>
            {slots.length > 0 && (
              <>
                {legend("full", "Full")}
                {legend("partial", "Partial")}
                {legend("none", "None")}
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 2, height: 11, background: "#dc2626" }} />
                  Snapshot (click)
                </span>
              </>
            )}
          </span>
          {frameMsg && <span style={{ fontSize: 12, color: "#b45309" }}>{frameMsg}</span>}
          <button
            type="button"
            onClick={grabFrame}
            title="Pause on the current video frame and draw a line or zone on it"
            style={{ height: 32, padding: "0 12px", borderRadius: 6, border: "1px solid #1e3a53", background: "#1e3a53", color: "#fff", fontSize: 13, cursor: "pointer" }}
          >
            Draw line / zone
          </button>
          {mediaButton("video", "Video", VideocamOutlinedIcon)}
          {mediaButton("image", "Image", ImageOutlinedIcon)}
        </div>

        {/* kept mounted so playback state survives switching to Image */}
        <div ref={videoBoxRef} style={{ display: media === "video" ? "block" : "none", borderTop: "1px solid #e5e7eb" }}>{children}</div>

        {media === "image" && (
          <div style={{ borderTop: "1px solid #e5e7eb", padding: 10, display: "flex", flexDirection: "column", gap: 8, color: "#1f2937" }}>
            {frame ? (
              <>
                <SnapshotFrame
                  key={frame.url}
                  url={frame.url}
                  caption={frame.label}
                  downloadName={`${uuid}-${date}-frame.jpg`}
                  tunnelUrl={tunnelUrl || ""}
                  userName={userName}
                  startEditing
                />
                <button
                  type="button"
                  onClick={() => setFrame(null)}
                  style={{ alignSelf: "flex-start", border: "none", background: "none", color: "#ED6926", fontSize: 12, cursor: "pointer", padding: 0 }}
                >
                  ← Back to snapshots
                </button>
              </>
            ) : current ? (
              <SnapshotFrame
                url={current.url}
                caption={hhmm(current.time)}
                downloadName={`${uuid}-${date}-${current.time}.jpg`}
                tunnelUrl={tunnelUrl || ""}
                userName={userName}
              />
            ) : (
              <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280", fontSize: 13, background: "#f3f4f6", borderRadius: 4 }}>
                No snapshot for this day yet. To draw a line or zone, play the video and press "Draw line / zone".
              </div>
            )}
            {snapshots.length > 0 && (
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
                {[...snapshots].reverse().map((s) => {
                  const on = s.time === current?.time;
                  return (
                    <button
                      key={s.time}
                      type="button"
                      onClick={() => {
                        setFrame(null);
                        setSelected(s.time);
                      }}
                      style={{ flex: "0 0 auto", width: 106, padding: 0, border: `2px solid ${on ? "#ED6926" : "#e5e7eb"}`, borderRadius: 4, background: "#fff", cursor: "pointer", overflow: "hidden" }}
                    >
                      <img src={s.url} alt={`Snapshot ${hhmm(s.time)}`} loading="lazy" style={{ width: "100%", height: 58, objectFit: "cover", display: "block", background: "#111" }} />
                      <span style={{ display: "block", fontSize: 12, padding: "3px 0", color: "#1f2937", fontFamily: "'Roboto Mono', monospace" }}>{hhmm(s.time)}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="timeline-wrapper">
              <ProgressScale
                value={current ? hhmmssToSeconds(current.time) : 0}
                segments={[]}
                onChange={onImageStripChange}
                independentZoom
              />
            </div>
          </div>
        )}
      </div>
    </RecordingTimelineContext.Provider>
  );
};

export default CameraSection;
